import { useEffect, useRef, useState } from "react";
import {
  CompaingsDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { CampaignsService } from "../../services/campaings.service";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import AddProductModal from "./add-product/AddProductModal";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import $axios from "../../api/accessor";

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState<CompaingsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<CompaingsDataType | null>(
    null
  );
  const [innerImageFileList, setInnerImageFileList] = useState<any[]>([]);

  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [addProductModal, setAddProductModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<CompaingsDataType>();
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [title, setTitle] = useState("");

  // Hər dil üçün şəkil siyahıları
  const [translationImages, setTranslationImages] = useState<{
    [key: string]: { cover: any[]; inner: any[] };
  }>({});

  const initialCampValues = useRef({
    name: "",
    description: "",
    text: "",
    start_date: "",
    end_date: "",
    cover_image: "",
    image: "",
    translations: [] as {
      lang_code: string;
      name: string;
      text: string;
      description: string;
      cover_image?: string;
      image?: string;
    }[],
  });

  useEffect(() => {
    setLoading(true);
    CampaignsService.getAllCampaigns()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: CompaingsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setCampaigns(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await CampaignsService.getAllCampaigns();
    setCampaigns(
      updatedData?.data?.data?.map(
        (item: CompaingsDataType, index: number) => ({
          ...item,
          key: index + 1,
        })
      )
    );
  };

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);

  const {
    handleSubmit,
    handleChange,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      name: "",
      description: "",
      text: "",
      translations: [] as {
        lang_code: string;
        name: string;
        text: string;
        description: string;
        cover_image?: string;
        image?: string;
      }[],
      start_date: "",
      end_date: "",
      cover_image: "",
      image: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Kompaniya adı tələb olunur"),
      start_date: Yup.string().required(
        "Kompaniya başlama tarixi tələb olunur"
      ),
      end_date: Yup.string().required("Kompaniya bitmə tarixi tələb olunur"),
      cover_image: Yup.string().required("Kompaniya şəkili tələb olunur"),
      image: Yup.string().required("Daxili şəkil tələb olunur"),
    }),

    onSubmit: async (values) => {
      // 🔧 translations-i JSON olaraq düz qur: boş image/cover_image göndərmə
      const updatedTranslations = [
        // Digər dillər (AZ xaric)
        ...values.translations
          .filter((t) => t.lang_code && t.lang_code !== "AZ")
          .map((t) => {
            const base: any = {
              lang_code: t.lang_code,
              name: t.name,
              text: t.text,
              description: t.description,
            };

            if (t.cover_image) base.cover_image = t.cover_image;
            if (t.image) base.image = t.image;

            return base;
          }),

        // AZ dili üçün ayrıca obyekt
        (() => {
          const base: any = {
            lang_code: "AZ",
            name: values.name,
            text: values.text,
            description: values.description,
          };

          if (values.cover_image) base.cover_image = values.cover_image;
          if (values.image) base.image = values.image;

          return base;
        })(),
      ];

      const finalValues = {
        name: values.name,
        description: values.description,
        text: values.text,
        start_date: values.start_date,
        end_date: values.end_date,
        cover_image: values.cover_image,
        image: values.image,
        translations: updatedTranslations,
      };

      if (isEditing && editingRecord) {
        // Hər şey eynidirsə API göndərmə
        const isEqual =
          finalValues.name === initialCampValues.current.name &&
          finalValues.description === initialCampValues.current.description &&
          finalValues.text === initialCampValues.current.text &&
          finalValues.start_date ===
            formatDate(initialCampValues.current.start_date) &&
          finalValues.end_date ===
            formatDate(initialCampValues.current.end_date) &&
          JSON.stringify(finalValues.translations) ===
            JSON.stringify(initialCampValues.current.translations);

        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          // 🔥 UPDATE PAYLOAD — yalnız dəyişənlər göndərilir
          const payload: any = {
            id: editingRecord.id,
            name: values.name,
            description: values.description,
            text: values.text,
            start_date: values.start_date,
            end_date: values.end_date,
            translations: [],
          };

          // ------------ 1️⃣ COVER_IMAGE yalnız dəyişibsə --------------
          if (editingRecord.cover_image !== values.cover_image) {
            payload.cover_image = values.cover_image;
          }

          // ------------ 2️⃣ IMAGE yalnız dəyişibsə ---------------------
          if (editingRecord.image !== values.image) {
            payload.image = values.image;
          }

          // ------------ 3️⃣ TRANSLATIONS ------------------------------

          // AZ dili dəyişibsə əlavə et
          const azChanged =
            values.name !== initialCampValues.current.name ||
            values.text !== initialCampValues.current.text ||
            values.description !== initialCampValues.current.description ||
            editingRecord.cover_image !== values.cover_image ||
            editingRecord.image !== values.image;

          if (azChanged) {
            payload.translations.push({
              lang_code: "AZ",
              name: values.name,
              text: values.text,
              description: values.description,
              ...(editingRecord.cover_image !== values.cover_image && {
                cover_image: values.cover_image,
              }),
              ...(editingRecord.image !== values.image && {
                image: values.image,
              }),
            });
          }

          // Digər dillər üçün dəyişənləri əlavə et
          values.translations.forEach((tr) => {
            const old = initialCampValues.current.translations.find(
              (t) => t.lang_code === tr.lang_code
            );

            const changed =
              !old ||
              old.name !== tr.name ||
              old.text !== tr.text ||
              old.description !== tr.description ||
              old.cover_image !== tr.cover_image ||
              old.image !== tr.image;

            if (changed) {
              payload.translations.push({
                lang_code: tr.lang_code,
                name: tr.name,
                text: tr.text,
                description: tr.description,
                ...(tr.cover_image && { cover_image: tr.cover_image }),
                ...(tr.image && { image: tr.image }),
              });
            }
          });

          // 🔥 UPDATE REQUEST
          const response = await CampaignsService.updateCampaign(payload);

          if (response.status === 200) {
            message.success("Kompaniya uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error("Kompaniyanı yeniləmək alınmadı.");
          }
        }

        setLoading(false);
        return;
      } else {
        const response = await CampaignsService.addCampaign(finalValues);
        if (response.status === 200) {
          message.success("Kompaniya uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(
            response.errorMessage || "Kompaniya əlavə etmək alınmadı."
          );
        }
      }

      setLoading(false);
    },
  });

  const getCurrentTranslation = () => {
    if (langCode === "AZ") {
      return {
        name: values.name,
        text: values.text,
        description: values.description,
      };
    }
    return (
      values.translations.find((t) => t.lang_code === langCode) || {
        name: "",
        text: "",
        description: "",
      }
    );
  };

  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === "AZ") {
      setFieldValue(field, value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        const newTranslation: any = {
          lang_code: langCode,
          name: "",
          text: "",
          description: "",
          // cover_image / image sonradan handleTranslationChange ilə set olunacaq
        };
        newTranslation[field] = value;
        updatedTranslations.push(newTranslation);
      }

      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleDeleteClick = async (record: CompaingsDataType) => {
    try {
      await CampaignsService.deleteCompaing(record.id.toString());
      setCampaigns((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Kompaniya uğurla silindi!");
    } catch (error) {
      message.error("Kompaniyani silmək alınmadı.");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return dateString.split(" ")[0];
  };

  const columns: TableProps<CompaingsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => (a.key ?? 0) - (b.key ?? 0),
    },
    {
      title: "Adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Təsvir",
      dataIndex: "description",
      render: (_, record) => (
        <div>
          {record.description?.length > 20
            ? record.description.substring(0, 20) + "..."
            : record.description}
        </div>
      ),
    },
    {
      title: "Mətn",
      dataIndex: "text",
      render: (text) => (
        <div>
          {text && text.length > 30 ? `${text.substring(0, 30)}...` : text}
        </div>
      ),
    },
    {
      title: "Başlanğıc tarixi",
      dataIndex: "start_date",
      render: (date) => <div> {formatDate(date)} </div>,
    },
    {
      title: "Bitmə tarixi",
      dataIndex: "end_date",
      render: (date) => <div> {formatDate(date)} </div>,
    },
    {
      title: "Şəkil",
      dataIndex: "cover_image",
      render: (cover_image, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.cover_image);
            setShowImageModal(true);
          }}
        >
          {record.cover_image ? (
            <img
              style={{ width: "40px" }}
              src={record.cover_image}
              alt={`${record.name}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Daxili şəkil",
      dataIndex: "image",
      render: (image, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.image);
            setShowImageModal(true);
          }}
        >
          {record.image ? (
            <img
              style={{ width: "40px" }}
              src={record.image}
              alt={`${record.name}`}
            />
          ) : null}
        </div>
      ),
    },

    {
      title: "Məhsullar",
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div
            className="add"
            onClick={() => {
              setTitle(record.name);
              setAddProductModal(true);
              setSelectedCampaign(record);
            }}
          >
            <i className="fa-solid fa-square-plus"></i>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu kompaniyani siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
    setFieldValue("cover_image", "");
    setFieldValue("image", "");
    setFieldValue("translations", []);
    setLogoFileList([]);
    setInnerImageFileList([]);
    setTranslationImages({});
  };

  const handleEditClick = (record: CompaingsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("description", record.description);
    setFieldValue("text", record.text);
    setFieldValue("start_date", formatDate(record.start_date));
    setFieldValue("end_date", formatDate(record.end_date));
    setFieldValue("cover_image", record.cover_image);
    setFieldValue("image", record.image);
    setFieldValue("translations", record.translations || []);
    setLangCode("AZ");

    initialCampValues.current = {
      name: record.name,
      description: record.description,
      text: record.text,
      start_date: record.start_date,
      end_date: record.end_date,
      cover_image: record.cover_image,
      image: record.image,
      translations:
        record.translations?.map((l) => ({
          lang_code: l.lang_code,
          name: l.name,
          text: l.text,
          description: l.description,
          cover_image: l.cover_image || "",
          image: l.image || "",
        })) || [],
    };

    setLogoFileList(
      record.cover_image
        ? [
            {
              uid: "-1",
              name: `${record.name}`,
              status: "done",
              url: record.cover_image,
            },
          ]
        : []
    );

    setInnerImageFileList(
      record.image
        ? [
            {
              uid: "-1",
              name: `${record.name}`,
              status: "done",
              url: record.image,
            },
          ]
        : []
    );

    // Tərcümə şəkillərini yükləyin
    const imgData: { [key: string]: { cover: any[]; inner: any[] } } = {};
    record.translations?.forEach((translation) => {
      imgData[translation.lang_code] = {
        cover: translation.cover_image
          ? [
              {
                uid: `-cover-${translation.lang_code}`,
                name: `${translation.name} cover`,
                status: "done",
                url: translation.cover_image,
              },
            ]
          : [],
        inner: translation.image
          ? [
              {
                uid: `-inner-${translation.lang_code}`,
                name: `${translation.name} inner`,
                status: "done",
                url: translation.image,
              },
            ]
          : [],
      };
    });
    setTranslationImages(imgData);
  };

  const updateCampaignProducts = (
    campaignId: number,
    finalProducts: { product_id: number }[]
  ) => {
    const updatedProducts = finalProducts.map((product) => ({
      ...product,
      campaign_id: campaignId,
    }));
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) => {
        if (campaign.id === campaignId) {
          return {
            ...campaign,
            products: updatedProducts,
          };
        }
        return campaign;
      })
    );
    setSelectedCampaign((prev) => {
      if (prev && prev.id === campaignId) {
        return { ...prev, products: updatedProducts };
      }
      return prev;
    });
  };

  const currentTranslation = getCurrentTranslation();

  return (
    <div id="pages">
      <Helmet>
        <title>BSC | Kampaniyalar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Kampaniyalar</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;

                  const items = Array.from(campaigns);
                  const [reorderedItem] = items.splice(result.source.index, 1);
                  items.splice(result.destination.index, 0, reorderedItem);

                  setCampaigns(items);
                  try {
                    const orderIds = items.map((i) => i.id);
                    const movedId = reorderedItem?.id;
                    const newIndex = result.destination.index;
                    console.log("Campaigns new order (IDs):", orderIds);
                    console.log(
                      `Moved ID: ${movedId}, from ${result.source.index} -> ${result.destination.index} (newIndex: ${newIndex})`
                    );
                  } catch {}

                  // Send reorder to API
                  try {
                    const order = items.map((i, idx) => ({
                      id: i.id,
                      sort_index: idx + 1,
                    }));
                    (async () => {
                      try {
                        const { data, status } = await $axios.post(
                          "/admin/campaigns/reorder",
                          { order }
                        );
                        console.log("Reorder saved:", status, data);
                      } catch (err: any) {
                        console.error(
                          "Reorder failed:",
                          err?.response?.status,
                          err?.response?.data || err?.message
                        );
                        message.error(
                          "Sıralama yadda saxlanmadı. Yenidən cəhd edin."
                        );
                      }
                    })();
                  } catch {}
                }}
              >
                <Table
                  scroll={{ x: "100%" }}
                  columns={columns}
                  dataSource={campaigns.map((item, index) => ({
                    ...item,
                    key: index + 1,
                  }))}
                  rowKey={(record) => record.id.toString()}
                  pagination={false}
                  components={{
                    body: {
                      wrapper: (bodyProps: any) => (
                        <Droppable droppableId="campaigns" direction="vertical">
                          {(dropProvided) => (
                            <tbody
                              {...bodyProps}
                              ref={dropProvided.innerRef}
                              {...dropProvided.droppableProps}
                            >
                              {bodyProps.children}
                              {dropProvided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      ),
                      row: (props: any) => {
                        const rowKey = props["data-row-key"];
                        const rowIndex = campaigns.findIndex(
                          (i) => i.id.toString() === rowKey.toString()
                        );
                        return (
                          <Draggable
                            draggableId={rowKey.toString()}
                            index={rowIndex}
                          >
                            {(dragProvided, snapshot) => (
                              <tr
                                {...props}
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                style={{
                                  ...props.style,
                                  ...dragProvided.draggableProps.style,
                                  cursor: snapshot.isDragging
                                    ? "grabbing"
                                    : "grab",
                                  background: snapshot.isDragging
                                    ? "#ffffff"
                                    : undefined,
                                  boxShadow: snapshot.isDragging
                                    ? "0 8px 20px rgba(0,0,0,0.18)"
                                    : undefined,
                                  transition:
                                    "transform 200ms ease, box-shadow 200ms ease, background-color 200ms ease",
                                }}
                              >
                                {props.children}
                              </tr>
                            )}
                          </Draggable>
                        );
                      },
                    },
                  }}
                />
              </DragDropContext>
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          isEditing ? "Kompaniyani redaktə edin" : "Yeni Kompaniya əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Kompaniya adı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Kompaniya adı"
                value={currentTranslation.name}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "name")
                }
              />
              <span className="focus_border"></span>
            </div>
            {langCode === "AZ" && (
              <ErrorMessage error={errors.name} touched={touched.name} />
            )}
          </div>
          <div className="form_input">
            <label className="label" htmlFor="description">
              Kompaniya təsviri {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="description"
                id="description"
                placeholder="Kompaniya təsviri"
                value={currentTranslation.description}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "description")
                }
              />
              <span className="focus_border"></span>
            </div>
          </div>
          <div className="form_input">
            <label className="label" htmlFor="text">
              Kompaniya mətni {langCode}
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                name="text"
                id="text"
                placeholder="Kompaniya mətni"
                value={currentTranslation.text}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "text")
                }
              />
              <span className="focus_border"></span>
            </div>
          </div>

          {langCode === "AZ" && (
            <>
              <div className="form_input">
                <label className="label" htmlFor="start_date">
                  Başlanğıc tarixi
                </label>
                <div className="input_wrapper">
                  <input
                    type="date"
                    name="start_date"
                    id="start_date"
                    value={values.start_date || ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage
                  error={errors.start_date}
                  touched={touched.start_date}
                />
              </div>
              <div className="form_input">
                <label className="label" htmlFor="end_date">
                  Bitmə tarixi
                </label>
                <div className="input_wrapper">
                  <input
                    type="date"
                    name="end_date"
                    id="end_date"
                    value={values.end_date}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage
                  error={errors.end_date}
                  touched={touched.end_date}
                />
              </div>
            </>
          )}

          {/* AZ dili üçün şəkillər */}
          {langCode === "AZ" && (
            <div className="select_images">
              <div>
                <SelectImage
                  image_name="Əsas şəkili seçin"
                  logoFileList={logoFileList}
                  setLogoFileList={setLogoFileList}
                  field="cover_image"
                  setFieldValue={setFieldValue}
                />
                <ErrorMessage
                  error={errors.cover_image}
                  touched={touched.cover_image}
                />
              </div>
              <div>
                <SelectImage
                  image_name="Daxili şəkili seçin"
                  logoFileList={innerImageFileList}
                  setLogoFileList={setInnerImageFileList}
                  field="image"
                  setFieldValue={setFieldValue}
                />
                <ErrorMessage error={errors.image} touched={touched.image} />
              </div>
            </div>
          )}

          {/* Digər dillər üçün cover və daxili şəkillər */}
          {langCode !== "AZ" && (
            <div className="select_images">
              <div>
                <SelectImage
                  image_name={`${langCode} üçün cover şəkil`}
                  logoFileList={translationImages[langCode]?.cover || []}
                  setLogoFileList={(files: any) => {
                    const fileList =
                      typeof files === "function"
                        ? files(translationImages[langCode]?.cover || [])
                        : files;
                    setTranslationImages((prev) => ({
                      ...prev,
                      [langCode]: {
                        cover: fileList,
                        inner: prev[langCode]?.inner || [],
                      },
                    }));
                  }}
                  field={`translation_cover_${langCode}`}
                  setFieldValue={(field, value) => {
                    handleTranslationChange(value, "cover_image");
                  }}
                />
              </div>
              <div>
                <SelectImage
                  image_name={`${langCode} üçün daxili şəkil`}
                  logoFileList={translationImages[langCode]?.inner || []}
                  setLogoFileList={(files: any) => {
                    const fileList =
                      typeof files === "function"
                        ? files(translationImages[langCode]?.inner || [])
                        : files;
                    setTranslationImages((prev) => ({
                      ...prev,
                      [langCode]: {
                        cover: prev[langCode]?.cover || [],
                        inner: fileList,
                      },
                    }));
                  }}
                  field={`translation_inner_${langCode}`}
                  setFieldValue={(field, value) => {
                    handleTranslationChange(value, "image");
                  }}
                />
              </div>
            </div>
          )}

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
      <AddProductModal
        title={title}
        campaignId={selectedCampaign?.id ?? 0}
        productIds={
          selectedCampaign?.products?.map((product) => product.product_id) || []
        }
        addProductModal={addProductModal}
        setAddProductModal={setAddProductModal}
        onUpdateCampaignProducts={updateCampaignProducts}
      />
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Campaigns;
